import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { fetchCustomerProfile } from '../utils/api';
import { useCustomer } from '../contexts/customer-context';
import Header from './Header';

const StyledContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const Logo = styled('img')(({ theme }) => ({
  width: '150px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    marginBottom: '15px',
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  marginBottom: theme.spacing(2),
  border: `3px solid ${theme.palette.primary.main}`,
  [theme.breakpoints.up('sm')]: {
    width: 120,
    height: 120,
    marginBottom: theme.spacing(3),
    border: `4px solid ${theme.palette.primary.main}`,
  },
}));

function CustomerWelcomePage() {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { customerId } = useCustomer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {
      console.log('Fetching customer data...');
      const profileData = await fetchCustomerProfile(customerId);
      setCustomerData(profileData);
      console.log('customerData: ', profileData);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      if (error.response.status === 404) {
        console.log('Profile not found');
        navigate('/profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const getCheerfulMessage = (reviewCount) => {
    if (reviewCount === 0) {
      return "Ready to share your first review? We can't wait to hear from you!";
    } else if (reviewCount === 1) {
      return "Awesome! You've shared your first review. Keep the momentum going!";
    } else if (reviewCount === 5) {
      return "High five! You've reached your first milestone with 5 reviews. You're making a real impact!";
    } else if (reviewCount === 10) {
      return "Double digits! With 10 reviews, you're now a seasoned contributor. Amazing work!";
    } else if (reviewCount === 25) {
      return "Wow! 25 reviews - you're a quarter to a hundred! Your insights are invaluable to our community.";
    } else if (reviewCount === 50) {
      return "Incredible! You've hit the big 5-0 in reviews. You're a true pillar of our community!";
    } else if (reviewCount === 100) {
      return "Phenomenal! 100 reviews - you've reached legendary status! Your contributions are truly remarkable.";
    } else if (reviewCount > 100) {
      return `Extraordinary! With ${reviewCount} reviews, you're in a league of your own. Thank you for your outstanding contributions!`;
    } else {
      return `Great job! You've shared ${reviewCount} reviews. Keep up the fantastic work!`;
    }
  };


  if (loading) {
    return (
      <>
        <Header />
        <StyledContainer maxWidth="sm">
          <CircularProgress />
        </StyledContainer>
      </>
    );
  }

  //const nextMilestone = getNextMilestone(customerData.totalReviews);

  return (
    <>
      <Header />
      <Fade in={true} timeout={1000}>
        <StyledContainer maxWidth="sm">
          {/*<Logo src="/path/to/your/logo.png" alt="Company Logo" />*/}
          <StyledAvatar src={''} alt={customerData.name} />
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Welcome back, {customerData.name}!
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: { xs: 2, sm: 4 }, color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            We're thrilled to see you again. Your feedback makes a difference!
          </Typography>
          <EmojiEventsIcon color="secondary" sx={{ fontSize: { xs: 36, sm: 48 }, mb: 2 }} />
          <Typography variant="h5" align="center" sx={{ fontWeight: 600, color: 'secondary.main', mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Your Review Stats
          </Typography>
          <Typography variant="h3" align="center" sx={{ fontWeight: 700, color: 'primary.main', mb: 2, fontSize: { xs: '2.5rem', sm: '3rem' } }}>
            
            {customerData.totalReviews}
            
          </Typography>
          <Typography variant="body1" align="center" sx={{ color: 'text.primary', fontWeight: 500, mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Total reviews submitted
          </Typography>
          <Typography variant="body1" align="center" sx={{ color: 'secondary.main', fontStyle: 'italic', mb: 4, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {getCheerfulMessage()}
          </Typography>
          {/*}
          <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 2 }}>
            Next milestone: {nextMilestone} reviews
          </Typography>
          */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 2, sm: 4 }, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, width: '100%' }}>
            <CardGiftcardIcon color="primary" sx={{ fontSize: { xs: 24, sm: 32 }, mr: 2 }} />
            <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Stay tuned for special perks and offers from our partners!
            </Typography>
          </Box>
        </StyledContainer>
      </Fade>
    </>
  );
}

export default CustomerWelcomePage;